// src/pages/Home.js
// Page d'accueil Fynho avec repli automatique sur données de démo - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Logo from '../components/shared/Logo';
import CategoryBar from '../components/home/CategoryBar';
import SearchBar from '../components/home/SearchBar';
import GeoButton from '../components/home/GeoButton';
import ListingRow from '../components/listing/ListingRow';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import ThemeToggle from '../components/shared/ThemeToggle';
import { useTheme } from '../hooks/useTheme';
import apiClient from '../api/client';
import DEMO_LISTINGS from '../data/demoListings';
import { generateOrganizationSchema } from '../utils/seo';
import ROUTES from '../constants/routes';

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [allListings, setAllListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadListings = async () => {
      setLoading(true);
      try {
        const result = await apiClient.get('/endpoints/listings.php?action=list&status=published&limit=40');
        setAllListings(result.listings && result.listings.length > 0 ? result.listings : DEMO_LISTINGS);
      } catch (err) {
        // API indisponible (backend pas encore hébergé) → repli sur les données de démo
        setAllListings(DEMO_LISTINGS);
      } finally {
        setLoading(false);
      }
    };
    loadListings();
  }, []);

  const sejours = allListings.filter((l) => l.category === 'sejours');
  const locations = allListings.filter((l) => l.category === 'locations');
  const immobilier = allListings.filter((l) => l.category === 'immobilier');
  const services = allListings.filter((l) => l.category === 'services');

  const organizationSchema = generateOrganizationSchema();

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: theme.bg,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      position: 'sticky',
      top: 0,
      backgroundColor: theme.bg,
      zIndex: 50,
      borderBottom: `1px solid ${theme.borderSoft}`,
    },
    headerControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    hero: {
      padding: '20px 16px 12px',
    },
    heroTitle: {
      fontSize: '26px',
      fontWeight: '700',
      color: theme.textPrimary,
      lineHeight: '1.2',
      marginBottom: '6px',
    },
    heroGold: {
      color: theme.gold,
    },
    heroSubtitle: {
      fontSize: '14px',
      color: theme.textSecondary,
      lineHeight: '1.4',
    },
    rowsContainer: {
      paddingTop: '8px',
    },
    stateMessage: {
      textAlign: 'center',
      padding: '48px 24px',
      color: theme.textSecondary,
      fontSize: '14px',
    },
    footer: {
      padding: '32px 16px 24px',
      textAlign: 'center',
      borderTop: `1px solid ${theme.borderSoft}`,
    },
    footerText: {
      fontSize: '12px',
      color: theme.textMuted,
      fontWeight: '500',
    },
    footerYear: {
      fontSize: '11px',
      color: theme.textMuted,
      marginTop: '8px',
    },
    mapButton: {
      position: 'fixed',
      bottom: '84px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 20px',
      backgroundColor: theme.textPrimary,
      color: theme.bg,
      border: 'none',
      borderRadius: '24px',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      zIndex: 95,
      boxShadow: theme.shadow,
    },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Fynho - Find Your New Home | Immobilier au Cameroun</title>
        <meta name="description" content="Trouvez votre logement idéal au Cameroun : séjours, locations, terrains et services à Douala, Yaoundé et plus." />
        <meta name="keywords" content="immobilier Cameroun, location Douala, location Yaoundé, terrain à vendre Cameroun, séjour Cameroun" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Fynho - Find Your New Home" />
        <meta property="og:description" content="Plateforme immobilière moderne au Cameroun" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fynho.com/" />
        <link rel="canonical" href="https://fynho.com/" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      {/* Header */}
      <div style={styles.header}>
        <Logo size="medium" showText={true} />
        <div style={styles.headerControls}>
          <ThemeToggle compact />
          <LanguageSwitcher compact />
        </div>
      </div>

      {/* Hero */}
      <div style={styles.hero}>
        <div style={styles.heroTitle}>
          {t('home.heroTitlePart1')}{' '}
          <span style={styles.heroGold}>{t('home.heroTitleHighlight')}</span>
        </div>
        <div style={styles.heroSubtitle}>{t('home.heroSubtitle')}</div>
      </div>

      {/* Recherche */}
      <SearchBar />
      <GeoButton />

      {/* Catégories */}
      <CategoryBar />

      {/* État de chargement */}
      {loading && <div style={styles.stateMessage}>{t('common.loading')}</div>}

      {/* Rangées par catégorie */}
      {!loading && (
        <div style={styles.rowsContainer}>
          <ListingRow
            icon="🏨"
            title={t('sections.sejoursTitle')}
            description={t('sections.sejoursDesc')}
            listings={sejours}
            seeAllRoute={ROUTES.SEJOURS}
          />
          <ListingRow
            icon="🏠"
            title={t('sections.locationsTitle')}
            description={t('sections.locationsDesc')}
            listings={locations}
            seeAllRoute={ROUTES.LOCATIONS}
          />
          <ListingRow
            icon="🌍"
            title={t('sections.immobilierTitle')}
            description={t('sections.immobilierDesc')}
            listings={immobilier}
            seeAllRoute={ROUTES.IMMOBILIER}
          />
          <ListingRow
            icon="🛎️"
            title={t('sections.servicesTitle')}
            description={t('sections.servicesDesc')}
            listings={services}
            seeAllRoute={ROUTES.SERVICES}
          />
        </div>
      )}

      {/* Footer épuré */}
      <div style={styles.footer}>
        <div style={styles.footerText}>Fynho</div>
        <div style={styles.footerYear}>© {new Date().getFullYear()}</div>
      </div>

      {/* Bouton flottant Carte */}
      <button style={styles.mapButton} onClick={() => navigate(ROUTES.MAP_SEARCH)}>
        🗺️ {t('home.viewMap')}
      </button>
    </div>
  );
};

export default Home;